/**
 * Dependency-free locale matching and authored-locale-set validation.
 *
 * Stored translation keys are never rewritten. Canonicalization is used only
 * for comparison; every successful resolution returns the actual available
 * key supplied by the caller.
 */

export type LocaleResolutionMode = 'preview' | 'strict';
export type LocaleDirection = 'ltr' | 'rtl';

export type LocaleResolutionReason =
  | 'exact'
  | 'canonical'
  | 'alias'
  | 'script'
  | 'language'
  | 'group'
  | 'default'
  | 'first'
  | 'missing';

export interface LocaleResolution {
  requestedLocale: string;
  resolvedLocale: string | null;
  reason: LocaleResolutionReason;
  usedFallback: boolean;
}

export interface ResolveLocaleOptions {
  defaultLocale?: string;
  mode?: LocaleResolutionMode;
}

export type LocaleIssueCode =
  | 'default_required'
  | 'language_required'
  | 'invalid_locale'
  | 'duplicate_language'
  | 'canonical_collision'
  | 'language_not_in_project'
  | 'default_not_in_project'
  | 'default_not_in_languages';

export interface LocaleIssue {
  code: LocaleIssueCode;
  message: string;
  locale?: string;
  index?: number;
  conflictingLocale?: string;
}

export interface LocaleSet {
  defaultLanguage: string;
  languages: string[];
}

export type LocaleSetValidationResult =
  | { ok: true; value: LocaleSet }
  | { ok: false; issues: LocaleIssue[] };

interface LocaleKeyInfo {
  actual: string;
  trimmed: string;
  canonicalKey: string | null;
  language: string | null;
  script: string | null;
  region: string | null;
  isBaseLanguage: boolean;
}

export const LOCALE_ALIAS_MAP: Readonly<Record<string, string>> = Object.freeze({
  'es-419': 'es-MX',
  nb: 'no',
  nn: 'no',
  iw: 'he',
  in: 'id',
  no: 'nb',
  he: 'iw',
  id: 'in',
});

export const LOCALE_GROUPS: ReadonlyArray<readonly string[]> = [
  ['en-US', 'en-GB', 'en-AU', 'en-CA', 'en'],
  ['es-ES', 'es-MX', 'es-419', 'es'],
  ['pt-BR', 'pt-PT', 'pt'],
  ['fr', 'fr-CA'],
  ['zh-Hans', 'zh-Hant'],
  ['no', 'nb', 'nn'],
  ['de', 'de-AT', 'de-CH'],
  ['he', 'iw'],
  ['id', 'in'],
];

const LOCALE_ALIAS_PAIRS = Object.entries(LOCALE_ALIAS_MAP);
const RTL_SCRIPTS = new Set(['Arab', 'Hebr', 'Nkoo', 'Rohg', 'Syrc', 'Thaa', 'Adlm']);
const RTL_LANGUAGES = new Set(['ar', 'dv', 'fa', 'he', 'iw', 'ku', 'ps', 'sd', 'ug', 'ur', 'yi']);

// Intl accepts the canonical production tags. This fallback keeps valid
// private-use/custom BCP 47-style project tags (for example x-brand) usable.
const BCP47_FALLBACK_PATTERN = /^(?:[A-Za-z]{2,8}|[ixIX])(?:-[A-Za-z0-9]{1,8})+$/;

/**
 * Return a stable, case-insensitive comparison key for a locale code.
 * Returns null for an empty or structurally invalid code.
 */
export function canonicalLocaleMatchKey(code: string): string | null {
  const canonical = canonicalLocaleCode(code);
  return canonical ? canonical.toLowerCase() : null;
}

/** Resolve UI writing direction without changing the persisted locale key. */
export function localeDirection(code: string): LocaleDirection {
  const canonical = canonicalLocaleCode(code);
  if (!canonical) return 'ltr';
  try {
    const locale = new Intl.Locale(canonical);
    const script = locale.script || locale.maximize().script;
    if (script && RTL_SCRIPTS.has(script)) return 'rtl';
    return RTL_LANGUAGES.has(locale.language.toLowerCase()) ? 'rtl' : 'ltr';
  } catch {
    const language = canonical.split('-')[0]?.toLowerCase() ?? '';
    return RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
  }
}

/**
 * Count user-perceived characters consistently across editor and server
 * validation. `String.length` counts UTF-16 code units and would therefore
 * penalize emoji, combining marks, and several non-Latin scripts differently.
 */
export function countUnicodeGraphemes(value: string): number {
  type SegmenterInstance = { segment(input: string): Iterable<unknown> };
  type SegmenterConstructor = new (
    locale?: string | string[],
    options?: { granularity: 'grapheme' },
  ) => SegmenterInstance;
  const Segmenter = (Intl as unknown as { Segmenter?: SegmenterConstructor }).Segmenter;
  if (Segmenter) {
    return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(value)).length;
  }
  // Modern supported runtimes expose Intl.Segmenter. The code-point fallback
  // remains safer than UTF-16 length for an explicitly unsupported runtime.
  return Array.from(value).length;
}

/**
 * Validate and normalize the locale set of an authored entity.
 *
 * The returned codes are the actual keys from projectLocales. Canonicalization
 * is used to find equivalent keys, not to mutate persisted locale identity.
 */
export function validateLocaleSet(input: {
  defaultLanguage: string;
  languages: readonly string[];
  projectLocales: readonly string[];
}): LocaleSetValidationResult {
  const issues: LocaleIssue[] = [];
  const projectKeys = toLocaleKeyInfo(input.projectLocales);
  const normalizedLanguages: string[] = [];
  const seenCanonical = new Map<string, string>();
  const seenLiteral = new Set<string>();

  input.languages.forEach((rawLocale, index) => {
    const locale = rawLocale.trim();
    if (!locale) {
      issues.push({
        code: 'language_required',
        message: `Language at index ${index} is empty.`,
        index,
      });
      return;
    }

    const canonicalKey = canonicalLocaleMatchKey(locale);
    if (!canonicalKey) {
      issues.push({
        code: 'invalid_locale',
        message: `Language "${locale}" is not a valid locale code.`,
        locale,
        index,
      });
      return;
    }

    const literalKey = locale.toLowerCase();
    if (seenLiteral.has(literalKey)) {
      issues.push({
        code: 'duplicate_language',
        message: `Language "${locale}" is duplicated.`,
        locale,
        index,
      });
      return;
    }

    const canonicalConflict = seenCanonical.get(canonicalKey);
    if (canonicalConflict) {
      issues.push({
        code: 'canonical_collision',
        message: `Language "${locale}" resolves to the same locale as "${canonicalConflict}".`,
        locale,
        index,
        conflictingLocale: canonicalConflict,
      });
      return;
    }

    seenLiteral.add(literalKey);
    seenCanonical.set(canonicalKey, locale);

    const projectMatch = findExactOrCanonical(locale, projectKeys);
    if (!projectMatch) {
      issues.push({
        code: 'language_not_in_project',
        message: `Language "${locale}" is not configured for the project.`,
        locale,
        index,
      });
      return;
    }

    normalizedLanguages.push(projectMatch.actual);
  });

  const requestedDefault = input.defaultLanguage.trim();
  let normalizedDefault = requestedDefault;

  if (!requestedDefault) {
    issues.push({
      code: 'default_required',
      message: 'Default language is required.',
    });
  } else {
    const defaultCanonicalKey = canonicalLocaleMatchKey(requestedDefault);
    if (!defaultCanonicalKey) {
      issues.push({
        code: 'invalid_locale',
        message: `Default language "${requestedDefault}" is not a valid locale code.`,
        locale: requestedDefault,
      });
    } else {
      const projectMatch = findExactOrCanonical(requestedDefault, projectKeys);
      if (!projectMatch) {
        issues.push({
          code: 'default_not_in_project',
          message: `Default language "${requestedDefault}" is not configured for the project.`,
          locale: requestedDefault,
        });
      } else {
        normalizedDefault = projectMatch.actual;
      }

      const languageMatch = normalizedLanguages.find(
        locale => canonicalLocaleMatchKey(locale) === defaultCanonicalKey,
      );
      if (!languageMatch) {
        issues.push({
          code: 'default_not_in_languages',
          message: `Default language "${requestedDefault}" must be present in languages.`,
          locale: requestedDefault,
        });
      } else {
        normalizedDefault = languageMatch;
      }
    }
  }

  if (issues.length > 0) {
    return { ok: false, issues };
  }

  return {
    ok: true,
    value: {
      defaultLanguage: normalizedDefault,
      languages: normalizedLanguages,
    },
  };
}

/** Resolve a requested locale to an actual key present in availableKeys. */
export function resolveLocaleKey(
  requestedLocale: string,
  availableKeys: readonly string[],
  options: ResolveLocaleOptions = {},
): LocaleResolution {
  const requested = requestedLocale.trim();
  const available = toLocaleKeyInfo(availableKeys);
  const mode = options.mode ?? 'preview';

  if (available.length === 0) {
    return resolution(requested, null, 'missing');
  }

  if (requested) {
    const exact = available.find(candidate => candidate.trimmed === requested);
    if (exact) {
      return resolution(requested, exact.actual, 'exact');
    }

    const requestedCanonicalKey = canonicalLocaleMatchKey(requested);
    if (requestedCanonicalKey) {
      const canonical = available.find(candidate => candidate.canonicalKey === requestedCanonicalKey);
      if (canonical) {
        return resolution(requested, canonical.actual, 'canonical');
      }
    }

    if (mode === 'strict') {
      return resolution(requested, null, 'missing');
    }

    const alias = findAliasMatch(requested, available);
    if (alias) {
      return resolution(requested, alias.actual, 'alias');
    }

    const requestedInfo = localeKeyInfo(requested);
    const scriptMatch = findScriptMatch(requestedInfo, available);
    if (scriptMatch) {
      return resolution(requested, scriptMatch.actual, 'script');
    }

    const languageMatch = findLanguageMatch(requestedInfo, available);
    if (languageMatch) {
      return resolution(requested, languageMatch.actual, 'language');
    }

    const groupMatch = findGroupMatch(requestedInfo, available);
    if (groupMatch) {
      return resolution(requested, groupMatch.actual, 'group');
    }
  } else if (mode === 'strict') {
    return resolution(requested, null, 'missing');
  }

  const defaultMatch = options.defaultLocale
    ? findExactOrCanonical(options.defaultLocale, available)
    : null;
  if (defaultMatch) {
    return resolution(requested, defaultMatch.actual, 'default');
  }

  return resolution(requested, available[0].actual, 'first');
}

/** Resolve a locale-keyed value without truthiness-based fallback. */
export function resolveLocalizedValue<T>(
  translations: Readonly<Record<string, T>>,
  requestedLocale: string,
  options: ResolveLocaleOptions = {},
): { locale: string; value: T; reason: LocaleResolutionReason } | null {
  const resolved = resolveLocaleKey(requestedLocale, Object.keys(translations), options);
  if (!resolved.resolvedLocale) {
    return null;
  }

  return {
    locale: resolved.resolvedLocale,
    value: translations[resolved.resolvedLocale],
    reason: resolved.reason,
  };
}

function canonicalLocaleCode(code: string): string | null {
  const trimmed = code.trim();
  if (!trimmed) return null;

  try {
    return Intl.getCanonicalLocales(trimmed)[0] ?? null;
  } catch {
    return BCP47_FALLBACK_PATTERN.test(trimmed) ? trimmed : null;
  }
}

function localeKeyInfo(actual: string): LocaleKeyInfo {
  const trimmed = actual.trim();
  const canonicalCode = canonicalLocaleCode(trimmed);
  const parts = (canonicalCode ?? trimmed).split('-');
  const language = /^[A-Za-z]{1,8}$/.test(parts[0] ?? '')
    ? parts[0].toLowerCase()
    : null;
  let script: string | null = null;
  let region: string | null = null;

  for (const part of parts.slice(1)) {
    if (!script && /^[A-Za-z]{4}$/.test(part)) {
      script = part.toLowerCase();
      continue;
    }
    if (!region && (/^[A-Za-z]{2}$/.test(part) || /^\d{3}$/.test(part))) {
      region = part.toLowerCase();
      break;
    }
  }

  return {
    actual,
    trimmed,
    canonicalKey: canonicalCode?.toLowerCase() ?? null,
    language,
    script,
    region,
    isBaseLanguage: parts.length === 1,
  };
}

function toLocaleKeyInfo(keys: readonly string[]): LocaleKeyInfo[] {
  return keys
    .map(localeKeyInfo)
    .filter(candidate => candidate.trimmed.length > 0);
}

function findExactOrCanonical(locale: string, available: readonly LocaleKeyInfo[]): LocaleKeyInfo | null {
  const trimmed = locale.trim();
  if (!trimmed) return null;

  const exact = available.find(candidate => candidate.trimmed === trimmed);
  if (exact) return exact;

  const canonicalKey = canonicalLocaleMatchKey(trimmed);
  if (!canonicalKey) return null;
  return available.find(candidate => candidate.canonicalKey === canonicalKey) ?? null;
}

function findAliasMatch(requested: string, available: readonly LocaleKeyInfo[]): LocaleKeyInfo | null {
  const requestedKey = canonicalLocaleMatchKey(requested);
  if (!requestedKey) return null;

  for (const [aliasFrom, aliasTo] of LOCALE_ALIAS_PAIRS) {
    if (canonicalLocaleMatchKey(aliasFrom) !== requestedKey) continue;
    const aliasKey = canonicalLocaleMatchKey(aliasTo);
    if (!aliasKey) continue;
    const match = available.find(candidate => candidate.canonicalKey === aliasKey);
    if (match) return match;
  }
  return null;
}

function findScriptMatch(
  requested: LocaleKeyInfo,
  available: readonly LocaleKeyInfo[],
): LocaleKeyInfo | null {
  if (!requested.language || !requested.script || !requested.region) return null;
  const languageScriptKey = canonicalLocaleMatchKey(`${requested.language}-${requested.script}`);
  if (!languageScriptKey) return null;
  return available.find(candidate => candidate.canonicalKey === languageScriptKey) ?? null;
}

function findLanguageMatch(
  requested: LocaleKeyInfo,
  available: readonly LocaleKeyInfo[],
): LocaleKeyInfo | null {
  if (!requested.language) return null;

  if (requested.isBaseLanguage) {
    return available.find(candidate => candidate.language === requested.language) ?? null;
  }

  return available.find(candidate => (
    candidate.language === requested.language
    && candidate.isBaseLanguage
  )) ?? null;
}

function findGroupMatch(
  requested: LocaleKeyInfo,
  available: readonly LocaleKeyInfo[],
): LocaleKeyInfo | null {
  if (!requested.canonicalKey) return null;
  const languageKey = requested.language
    ? canonicalLocaleMatchKey(requested.language)
    : null;

  for (const group of LOCALE_GROUPS) {
    const groupKeys = group.map(canonicalLocaleMatchKey).filter((key): key is string => Boolean(key));
    if (!groupKeys.includes(requested.canonicalKey) && (!languageKey || !groupKeys.includes(languageKey))) {
      continue;
    }

    for (const memberKey of groupKeys) {
      if (memberKey === requested.canonicalKey) continue;
      const match = available.find(candidate => candidate.canonicalKey === memberKey);
      if (match) return match;
    }
  }

  return null;
}

function resolution(
  requestedLocale: string,
  resolvedLocale: string | null,
  reason: LocaleResolutionReason,
): LocaleResolution {
  return {
    requestedLocale,
    resolvedLocale,
    reason,
    usedFallback: reason !== 'exact' && reason !== 'canonical',
  };
}
